// src/pages/MjesecniSati.jsx
// Tablica mjesečnih sati po osobi: ukupno, redovni, nedjelja, prekovremeni

import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import Modal from 'react-modal';
import { AuthContext } from '../config/AuthProvider';
import { useLoading } from '../config/LoadingContext';
import Login from '../authPage/Login';
import { db } from '../config/firebase';
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  getRegularHoursForMonth,
  isSunday,
} from '../utils/monthUtils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FaFileExport, FaFileImport } from 'react-icons/fa';
import { formatDecimalHoursSigned } from '../utils/durationUtils';
import {
  monthlyNightHoursDocId,
  parseDecimalHoursInput,
} from '../utils/monthlyNightHours';
import {
  aggregateNightHoursFromSheet,
  buildExternalIdToUserMap,
} from '../utils/nightShiftExcelImport';
import { getWorkHoursFromMetric, parseGodisnjiHours } from '../utils/metricHours';
import '../App.css';
import '../css/MjesecniSati.css';

const MONTH_NAMES = [
  'Sij', 'Velj', 'Ožu', 'Tra', 'Svi', 'Lip', 'Srp', 'Kol', 'Ruj', 'Lis', 'Stu', 'Pro',
];

/** Puni naziv mjeseca (Excel / UI) */
const MONTH_NAMES_LONG = [
  'Siječanj',
  'Veljača',
  'Ožujak',
  'Travanj',
  'Svibanj',
  'Lipanj',
  'Srpanj',
  'Kolovoz',
  'Rujan',
  'Listopad',
  'Studeni',
  'Prosinac',
];

/** Za ime datoteke (bez dijakritike, sigurno za OS) */
const MONTH_NAMES_FILE = [
  'Sijecanj',
  'Veljaca',
  'Ozujak',
  'Travanj',
  'Svibanj',
  'Lipanj',
  'Srpanj',
  'Kolovoz',
  'Rujan',
  'Listopad',
  'Studeni',
  'Prosinac',
];

/** Sanitizacija dijela imena datoteke */
function slugifyFilePart(str) {
  const s = (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return (s || 'nepoznato').substring(0, 72);
}

function buildMjesečniSatiExportFileName(year, month, firmId, getFirmNameFn) {
  const mm = String(month).padStart(2, '0');
  const monthSlug = MONTH_NAMES_FILE[month - 1] || `mjesec-${month}`;
  const firmSlug = firmId
    ? `Firma-${slugifyFilePart(getFirmNameFn(firmId))}`
    : 'Sve-firme';
  return `Mjesečni-sati_${year}-${mm}_${monthSlug}_${firmSlug}.xlsx`;
}

function makeDateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getFirstNameWord(fullName) {
  const normalized = String(fullName || '').trim();
  if (!normalized) return '';
  return normalized.split(/\s+/)[0] || '';
}

/** ID meta-dokumenta u uploadedNightHoursMonths */
function uploadedNightMonthMetaDocId(year, month, firmId) {
  return `${year}_${String(month).padStart(2, '0')}_${firmId}`;
}

function detectCsvDelimiter(text) {
  const sample = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .find((line) => line.trim());
  if (!sample) return ',';
  let inQuotes = false;
  const counts = { ',': 0, ';': 0, '\t': 0 };
  for (let i = 0; i < sample.length; i++) {
    const ch = sample[i];
    if (ch === '"') {
      if (inQuotes && sample[i + 1] === '"') {
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (inQuotes) continue;
    if (ch === ',' || ch === ';' || ch === '\t') counts[ch] += 1;
  }
  if (counts[';'] >= counts[','] && counts[';'] >= counts['\t']) return ';';
  if (counts['\t'] >= counts[',']) return '\t';
  return ',';
}

function parseCsvRows(text, delimiter) {
  const src = String(text || '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === '"') {
      if (inQuotes && src[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && ch === delimiter) {
      row.push(field.trim());
      field = '';
      continue;
    }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(field.trim());
      rows.push(row);
      row = [];
      field = '';
      continue;
    }
    field += ch;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field.trim());
    rows.push(row);
  }
  return rows.filter((r) => r.some((v) => String(v || '').trim() !== ''));
}

async function loadNightImportWorksheet(file) {
  const fileName = String(file?.name || '').toLowerCase();
  if (fileName.endsWith('.csv')) {
    const csvText = await file.text();
    const delim = detectCsvDelimiter(csvText);
    const rows = parseCsvRows(csvText, delim);
    if (rows.length === 0) return null;
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('CSV');
    rows.forEach((r) => ws.addRow(r));
    return ws;
  }
  const workbook = new ExcelJS.Workbook();
  const buf = await file.arrayBuffer();
  await workbook.xlsx.load(buf);
  return workbook.worksheets[0] || null;
}

function formatFirestoreDate(ts) {
  if (!ts) return '—';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('hr-HR');
  } catch {
    return '—';
  }
}

function MjesecniSati() {
  const { currentUser } = useContext(AuthContext);
  const { showLoading, hideLoading } = useLoading();
  const [people, setPeople] = useState([]);
  const [firms, setFirms] = useState([]);
  const [dailyMetricsData, setDailyMetricsData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFirm, setSelectedFirm] = useState('');
  /** Mjesec za izvoz u Excel (1–12), neovisno o prikazu cijele godine u tablici */
  const [exportMonth, setExportMonth] = useState(() => new Date().getMonth() + 1);
  /** `${userId}_${month}` -> sati (decimal) za selectedYear */
  const [nightHoursByUserMonth, setNightHoursByUserMonth] = useState({});
  /** `yyyy-mm-dd` -> naziv praznika */
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [editingNight, setEditingNight] = useState(null);
  const [nightDraft, setNightDraft] = useState('');
  const [isNightImportModalOpen, setIsNightImportModalOpen] = useState(false);
  const [isNightUploadedModalOpen, setIsNightUploadedModalOpen] = useState(false);
  const [nightImportFirmId, setNightImportFirmId] = useState('');
  const [nightImportMonth, setNightImportMonth] = useState(() => new Date().getMonth() + 1);
  const [uploadedNightMonths, setUploadedNightMonths] = useState([]);
  const nightImportFileRef = useRef(null);

  const fetchFirms = async () => {
    showLoading('Učitavanje firmi...');
    try {
      const snapshot = await getDocs(collection(db, 'firms'));
      setFirms(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Greška pri dohvaćanju firmi:', e);
    } finally {
      hideLoading();
    }
  };

  const fetchUsers = async () => {
    showLoading('Učitavanje osoba...');
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      setPeople(
        snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name || '',
            firmId: data.firmId || 'default',
            glovoId: data.glovoId || '',
            woltId: data.woltId || '',
            startMonth: data.startMonth ?? 1,
            startYear: data.startYear ?? new Date().getFullYear(),
            archived: data.archived || false,
            archiveMonth: data.archiveMonth ?? null,
            archiveYear: data.archiveYear ?? null,
          };
        })
      );
    } catch (e) {
      console.error('Greška pri dohvaćanju korisnika:', e);
    } finally {
      hideLoading();
    }
  };

  const fetchDailyMetrics = async () => {
    showLoading('Učitavanje sati...');
    try {
      const years = [selectedYear - 1, selectedYear, selectedYear + 1];
      const q = query(
        collection(db, 'dailyMetrics'),
        where('year', 'in', years)
      );
      const snapshot = await getDocs(q);
      const metrics = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDailyMetricsData(metrics);
    } catch (e) {
      console.error('Greška pri dohvaćanju metrika:', e);
    } finally {
      hideLoading();
    }
  };

  const fetchMonthlyNightHours = async () => {
    try {
      const q = query(
        collection(db, 'monthlyNightHours'),
        where('year', '==', selectedYear)
      );
      const snapshot = await getDocs(q);
      const map = {};
      snapshot.docs.forEach((d) => {
        const data = d.data();
        const uid = data.userId;
        const m = data.month;
        if (uid != null && m >= 1 && m <= 12) {
          map[`${uid}_${m}`] = data.hours ?? 0;
        }
      });
      setNightHoursByUserMonth(map);
    } catch (e) {
      console.error('Greška pri dohvaćanju noćnih sati:', e);
    }
  };

  const fetchHolidays = async () => {
    try {
      const q = query(
        collection(db, 'holidays'),
        where('year', '==', selectedYear)
      );
      const snapshot = await getDocs(q);
      const map = {};
      snapshot.docs.forEach((d) => {
        const data = d.data();
        const key = data.dateKey || makeDateKey(data.year, data.month, data.day);
        if (key) map[key] = data.name || 'Praznik';
      });
      setHolidaysByDate(map);
    } catch (e) {
      console.error('Greška pri dohvaćanju praznika:', e);
    }
  };

  const fetchUploadedNightMonths = async () => {
    try {
      const years = [selectedYear - 1, selectedYear, selectedYear + 1];
      const q = query(
        collection(db, 'uploadedNightHoursMonths'),
        where('year', 'in', years)
      );
      const snapshot = await getDocs(q);
      setUploadedNightMonths(
        snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    } catch (e) {
      console.error('Greška pri dohvaćanju uvezenih mjeseci (noćni sati):', e);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchFirms();
      fetchUsers();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) fetchDailyMetrics();
  }, [currentUser, selectedYear]);

  useEffect(() => {
    if (currentUser) fetchMonthlyNightHours();
  }, [currentUser, selectedYear]);

  useEffect(() => {
    if (currentUser) fetchHolidays();
  }, [currentUser, selectedYear]);

  useEffect(() => {
    if (currentUser) fetchUploadedNightMonths();
  }, [currentUser, selectedYear]);

  const getFirmName = (firmId) => {
    const f = firms.find((x) => x.id === firmId);
    return f ? f.name : 'Default';
  };

  const filteredPeople = useMemo(() => {
    return people
      .filter((p) => {
        if (selectedFirm && p.firmId !== selectedFirm) return false;
        if (p.archived) {
          const archiveDate = new Date(p.archiveYear, (p.archiveMonth || 1) - 1);
          const endOfYear = new Date(selectedYear, 11, 31);
          if (archiveDate <= endOfYear) return false;
          return true;
        }
        const startYear = p.startYear ?? selectedYear;
        const startMonth = p.startMonth ?? 1;
        return startYear < selectedYear || (startYear === selectedYear && startMonth <= 12);
      })
      .sort((a, b) => {
        const fa = getFirmName(a.firmId);
        const fb = getFirmName(b.firmId);
        const firmCmp = fa.localeCompare(fb);
        if (firmCmp !== 0) return firmCmp;
        const nameCmp = getFirstNameWord(a.name).localeCompare(getFirstNameWord(b.name));
        if (nameCmp !== 0) return nameCmp;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
  }, [people, selectedFirm, selectedYear, firms]);

  /** Osobe odabrane firme za uvoz noćnih sati (isti filtar arhive/početka kao u tablici) */
  const peopleForNightImport = useMemo(() => {
    if (!nightImportFirmId) return [];
    return people
      .filter((p) => {
        if (p.firmId !== nightImportFirmId) return false;
        if (p.archived) {
          const archiveDate = new Date(p.archiveYear, (p.archiveMonth || 1) - 1);
          const endOfYear = new Date(selectedYear, 11, 31);
          if (archiveDate <= endOfYear) return false;
          return true;
        }
        const startYear = p.startYear ?? selectedYear;
        const startMonth = p.startMonth ?? 1;
        return startYear < selectedYear || (startYear === selectedYear && startMonth <= 12);
      })
      .sort((a, b) => {
        const nameCmp = getFirstNameWord(a.name).localeCompare(getFirstNameWord(b.name));
        if (nameCmp !== 0) return nameCmp;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
  }, [people, nightImportFirmId, selectedYear]);

  const rowTypes = [
    { key: 'total', label: 'Ukupno sati' },
    { key: 'regular', label: 'Redovni sati tog mjeseca' },
    { key: 'sunday', label: 'Odrađeni sati nedjeljom' },
    { key: 'night', label: 'Noćni sati' },
    { key: 'holiday', label: 'Praznici sati' },
    { key: 'godisnji', label: 'Godišnji sati' },
    { key: 'overtime', label: 'Prekovremeni sati ukupno' },
  ];

  const personMonthData = useMemo(() => {
    const result = {};
    filteredPeople.forEach((person) => {
      result[person.id] = {};
      for (let m = 1; m <= 12; m++) {
        const personMetrics = dailyMetricsData.filter(
          (d) => d.userId === person.id && d.year === selectedYear && d.month === m
        );
        let totalHours = 0;
        let sundayHours = 0;
        let holidayHours = 0;
        let godisnjiHours = 0;
        personMetrics.forEach((d) => {
          const godisnji = parseGodisnjiHours(d);
          if (godisnji > 0) {
            godisnjiHours += godisnji;
            totalHours += godisnji;
            return;
          }
          const h = getWorkHoursFromMetric(d);
          totalHours += h;
          if (isSunday(selectedYear, m, d.day)) sundayHours += h;
          if (holidaysByDate[makeDateKey(selectedYear, m, d.day)]) holidayHours += h;
        });
        const regularHours = getRegularHoursForMonth(selectedYear, m);
        const overtimeHours = Math.max(0, totalHours - regularHours);
        const nightHours = nightHoursByUserMonth[`${person.id}_${m}`] ?? 0;
        result[person.id][m] = {
          total: totalHours,
          regular: regularHours,
          sunday: sundayHours,
          night: nightHours,
          holiday: holidayHours,
          godisnji: godisnjiHours,
          overtime: overtimeHours,
        };
      }
    });
    return result;
  }, [filteredPeople, dailyMetricsData, selectedYear, nightHoursByUserMonth, holidaysByDate]);

  /** Zbroj godišnjeg po osobi za odabranu godinu (svi mjeseci). */
  const personGodisnjiYearTotal = useMemo(() => {
    const totals = {};
    filteredPeople.forEach((person) => {
      let sum = 0;
      for (let m = 1; m <= 12; m++) {
        sum += personMonthData[person.id]?.[m]?.godisnji ?? 0;
      }
      totals[person.id] = sum;
    });
    return totals;
  }, [filteredPeople, personMonthData]);

  const commitNightEdit = async (personId, month) => {
    const parsed = parseDecimalHoursInput(nightDraft);
    if (parsed === null) {
      alert('Unesi broj sati (npr. 2 ili 2,5).');
      return;
    }
    const docId = monthlyNightHoursDocId(personId, selectedYear, month);
    const ref = doc(db, 'monthlyNightHours', docId);
    try {
      if (parsed === 0) {
        try {
          await deleteDoc(ref);
        } catch (err) {
          if (err?.code !== 'not-found') throw err;
        }
        setNightHoursByUserMonth((prev) => {
          const next = { ...prev };
          delete next[`${personId}_${month}`];
          return next;
        });
      } else {
        await setDoc(ref, {
          userId: personId,
          year: selectedYear,
          month,
          hours: parsed,
        });
        setNightHoursByUserMonth((prev) => ({
          ...prev,
          [`${personId}_${month}`]: parsed,
        }));
      }
      setEditingNight(null);
      setNightDraft('');
    } catch (e) {
      console.error('Greška pri spremanju noćnih sati:', e);
      alert('Spremanje nije uspjelo.');
    }
  };

  const handleExportToExcel = async () => {
    const m = exportMonth;
    if (m < 1 || m > 12) {
      alert('Odaberi ispravan mjesec (1–12).');
      return;
    }

    showLoading('Izvoz u Excel...');
    try {
      const workbook = new ExcelJS.Workbook();
      const monthLong = MONTH_NAMES_LONG[m - 1];
      const sheetTitle = `${monthLong} ${selectedYear}`;
      const sheet = workbook.addWorksheet(sheetTitle.substring(0, 31));

      const valueHeader = `${monthLong} ${selectedYear}`;
      sheet.addRow([`Mjesečni sati – ${valueHeader}`]);
      sheet.mergeCells(1, 1, 1, 4);
      sheet.getRow(1).font = { bold: true, size: 12 };
      sheet.getRow(1).alignment = { vertical: 'middle' };

      const firmNote = selectedFirm
        ? `Firma: ${getFirmName(selectedFirm)}`
        : 'Firma: sve (filtar „Sve”)';
      sheet.addRow([firmNote]);
      sheet.mergeCells(2, 1, 2, 4);
      sheet.getRow(2).font = { italic: true, size: 10 };
      sheet.addRow([]);

      const headerRow = sheet.addRow([
        'Osoba',
        'Firma',
        'Vrsta',
        `${valueHeader} (sati i minute)`,
      ]);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

      filteredPeople.forEach((person) => {
        rowTypes.forEach((rt) => {
          const d = personMonthData[person.id]?.[m] ?? {};
          const val = d[rt.key] ?? 0;
          const num = typeof val === 'number' ? val : parseFloat(val) || 0;
          sheet.addRow([
            rt.key === 'total' ? person.name : '',
            rt.key === 'total' ? getFirmName(person.firmId) : '',
            rt.label,
            formatDecimalHoursSigned(num),
          ]);
        });
      });

      sheet.columns.forEach((col, i) => {
        col.width = i === 0 ? 28 : i === 1 ? 20 : i === 2 ? 32 : 18;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = buildMjesečniSatiExportFileName(
        selectedYear,
        m,
        selectedFirm,
        getFirmName
      );
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
    } catch (e) {
      console.error('Greška pri izvozu:', e);
      alert('Došlo je do greške prilikom izvoza u Excel.');
    } finally {
      hideLoading();
    }
  };

  const deleteUploadedNightMonth = async (row) => {
    const { id: metaId, year, month: mo, firmId } = row;
    if (
      !window.confirm(
        `Obrisati zapis uvezenih noćnih sati za ${getFirmName(firmId)} – ${MONTH_NAMES_LONG[mo - 1]} ${year}?\n\nTime će se obrisati svi noćni sati u bazi za tu firmu i taj mjesec (uključujući ručne unose za te osobe).`
      )
    ) {
      return;
    }
    showLoading('Brisanje...');
    try {
      const firmUserIds = new Set(
        people.filter((p) => p.firmId === firmId).map((p) => p.id)
      );
      const snap = await getDocs(
        query(collection(db, 'monthlyNightHours'), where('year', '==', year))
      );
      for (const d of snap.docs) {
        const data = d.data();
        if (data.month === mo && firmUserIds.has(data.userId)) {
          try {
            await deleteDoc(d.ref);
          } catch (err) {
            if (err?.code !== 'not-found') throw err;
          }
        }
      }
      await deleteDoc(doc(db, 'uploadedNightHoursMonths', metaId));
      setUploadedNightMonths((prev) => prev.filter((x) => x.id !== metaId));
      await fetchMonthlyNightHours();
      alert('Zapis i povezani noćni sati za taj mjesec su obrisani.');
    } catch (err) {
      console.error('Greška pri brisanju uvezenog mjeseca:', err);
      alert('Brisanje nije uspjelo.');
    } finally {
      hideLoading();
    }
  };

  const handleImportNightHoursExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const firmId = nightImportFirmId;
    const m = nightImportMonth;

    if (!firmId) {
      alert('Odaberi firmu za uvoz.');
      return;
    }
    if (m < 1 || m > 12) {
      alert('Odaberi ispravan mjesec (1–12).');
      return;
    }

    const idMap = buildExternalIdToUserMap(peopleForNightImport);
    if (idMap.size === 0) {
      alert('Za odabranu firmu nema osoba s Glovo ili Wolt ID-om. Dodaj ID-ove u profilima osoba.');
      return;
    }

    showLoading('Čitanje datoteke...');
    try {
      const sheet = await loadNightImportWorksheet(file);
      if (!sheet) {
        alert('Datoteka je prazna ili nema čitljivih redaka.');
        return;
      }

      const agg = aggregateNightHoursFromSheet(sheet, {
        year: selectedYear,
        month: m,
        externalIdToUserId: idMap,
      });

      if (!agg.ok) {
        alert(agg.error);
        return;
      }

      if (agg.nightRowsInMonth === 0) {
        alert(
          `Nema noćnih redaka u ${MONTH_NAMES_LONG[m - 1]} ${selectedYear}. Provjeri datume u stupcu Datum i vrijednost „Night“ u stupcu Vrijeme smjene.`
        );
        return;
      }

      const summary = [
        `Uvoz noćnih sati – ${getFirmName(firmId)}`,
        `Mjesec: ${MONTH_NAMES_LONG[m - 1]} ${selectedYear}`,
        `Noćni retci u datoteci (za taj mjesec): ${agg.nightRowsInMonth}`,
        `Osoba koje će biti ažurirane: ${agg.totals.size}`,
      ];
      if (agg.unknownIds.length > 0) {
        const sample = agg.unknownIds.slice(0, 20).join(', ');
        summary.push(
          `Neprepoznati ID brojevi (${agg.unknownIds.length}): ${sample}${agg.unknownIds.length > 20 ? ' …' : ''}`
        );
      }
      summary.push(
        '',
        'Za svaku prepoznatu osobu zbroj noćnog „Efektivni rad“ bit će spremljen u red Noćni sati (postojeća vrijednost za taj mjesec bit će zamijenjena).'
      );

      hideLoading();
      if (!window.confirm(summary.join('\n'))) return;

      showLoading('Spremanje noćnih sati...');
      const yearNightSnap = await getDocs(
        query(collection(db, 'monthlyNightHours'), where('year', '==', selectedYear))
      );
      const rogueDocIdsByKey = new Map();
      yearNightSnap.docs.forEach((d) => {
        const data = d.data();
        const uid = data.userId;
        const mo = data.month;
        if (uid == null || mo !== m || mo < 1 || mo > 12) return;
        const expectedId = monthlyNightHoursDocId(uid, selectedYear, m);
        if (d.id === expectedId) return;
        const key = `${uid}_${m}`;
        if (!rogueDocIdsByKey.has(key)) rogueDocIdsByKey.set(key, []);
        rogueDocIdsByKey.get(key).push(d.id);
      });

      let saved = 0;
      for (const [userId, rawHours] of agg.totals) {
        const hours = Math.round(rawHours * 10000) / 10000;
        const docId = monthlyNightHoursDocId(userId, selectedYear, m);
        const ref = doc(db, 'monthlyNightHours', docId);
        const key = `${userId}_${m}`;
        const rogueIds = rogueDocIdsByKey.get(key) || [];
        for (const rogueId of rogueIds) {
          try {
            await deleteDoc(doc(db, 'monthlyNightHours', rogueId));
          } catch (err) {
            if (err?.code !== 'not-found') throw err;
          }
        }

        if (!hours || hours < 1e-9) {
          try {
            await deleteDoc(ref);
          } catch (err) {
            if (err?.code !== 'not-found') throw err;
          }
        } else {
          const existing = await getDoc(ref);
          if (existing.exists()) {
            await updateDoc(ref, { hours });
          } else {
            await setDoc(ref, {
              userId,
              year: selectedYear,
              month: m,
              hours,
            });
          }
        }
        saved += 1;
      }

      const metaId = uploadedNightMonthMetaDocId(selectedYear, m, firmId);
      await setDoc(doc(db, 'uploadedNightHoursMonths', metaId), {
        year: selectedYear,
        month: m,
        firmId,
        filename: file.name,
        peopleUpdated: saved,
        importedAt: serverTimestamp(),
      });

      await fetchMonthlyNightHours();
      await fetchUploadedNightMonths();
      setIsNightImportModalOpen(false);
      alert(
        `Uvezeno: ${saved} osoba (${getFirmName(firmId)}). Noćni retci izvan odabranog mjeseca: ${agg.skippedOutsideMonth} (zanemareno).`
      );
    } catch (err) {
      console.error('Greška pri uvozu noćnih sati:', err);
      alert('Uvoz nije uspio. Provjeri je li datoteka .xlsx/.csv i sadrži očekivana zaglavlja.');
    } finally {
      hideLoading();
    }
  };

  const openNightImportModal = () => {
    setNightImportFirmId(selectedFirm || '');
    setNightImportMonth(exportMonth);
    setIsNightImportModalOpen(true);
  };

  // Zakomentirano po zahtjevu: privremeno skriven "Izvezi obračun (EUR)" na Mjesečni sati.
  // function colToLetter(colIndex) { ... }
  // const handleExportObracunExcel = async () => { ... };

  if (!currentUser) return <Login />;

  return (
    <div className="mjesecni-sati-container">
      <h1>Mjesečni sati</h1>

      <div className="mjesecni-sati-filters">
        <label className="mjesecni-sati-export-month-label">
          Izvoz za mjesec:
          <select
            value={exportMonth}
            onChange={(e) => setExportMonth(parseInt(e.target.value, 10))}
            aria-label="Mjesec za izvoz u Excel"
          >
            {MONTH_NAMES_LONG.map((name, i) => (
              <option key={i + 1} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="mjesecni-sati-export-btn"
          onClick={handleExportToExcel}
          title={
            selectedFirm
              ? `Izvezi ${MONTH_NAMES_LONG[exportMonth - 1]} ${selectedYear} – ${getFirmName(selectedFirm)}`
              : `Izvezi ${MONTH_NAMES_LONG[exportMonth - 1]} ${selectedYear} – sve firme`
          }
        >
          <FaFileExport />
          <span>Izvezi u Excel</span>
        </button>
        <button
          type="button"
          className="mjesecni-sati-import-btn"
          onClick={openNightImportModal}
          title="Odaberi firmu i mjesec, zatim uvezi datoteku (.xlsx/.csv) (Night + Efektivni rad)"
        >
          <FaFileImport />
          <span>Uvezi noćne sate (.xlsx/.csv)</span>
        </button>
        <button
          type="button"
          className="mjesecni-sati-uploaded-months-btn"
          onClick={() => setIsNightUploadedModalOpen(true)}
          title="Pregled uvezenih mjeseci noćnih sati po firmama"
        >
          <span>Uvezeni mjeseci (noćni sati)</span>
        </button>
        <input
          ref={nightImportFileRef}
          type="file"
          accept=".xlsx,.xls,.csv,text/csv"
          onChange={handleImportNightHoursExcel}
          hidden
        />
        {/* Zakomentirano po zahtjevu: gumb "Izvezi obračun (EUR)" */}
        <label>
          Godina:
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {Array.from({ length: 11 }, (_, i) => selectedYear - 5 + i)
              .filter((y) => y >= 2020 && y <= 2030)
              .map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
          </select>
        </label>
        <label>
          Firma:
          <select
            value={selectedFirm}
            onChange={(e) => setSelectedFirm(e.target.value)}
          >
            <option value="">Sve</option>
            {firms.filter((f) => f.id !== 'default').map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mjesecni-sati-table-wrapper">
        <table className="mjesecni-sati-table">
          <thead>
            <tr>
              <th className="col-person">Osoba</th>
              <th className="col-type">Vrsta</th>
              {MONTH_NAMES.map((name, i) => (
                <th key={i} className="col-month">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredPeople.map((person) => (
              <React.Fragment key={person.id}>
                {rowTypes.map((rt) => (
                  <tr key={`${person.id}-${rt.key}`} className={`row-${rt.key}`}>
                    {rt.key === 'total' ? (
                      <td
                        rowSpan={7}
                        className="cell-person"
                      >
                        {person.name}
                        <br />
                        <span className="firm-badge">{getFirmName(person.firmId)}</span>
                        <span className="godisnji-year-total">
                          Godišnji {selectedYear}:{' '}
                          {formatDecimalHoursSigned(personGodisnjiYearTotal[person.id] ?? 0)}
                        </span>
                      </td>
                    ) : null}
                    <td className="cell-type">{rt.label}</td>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
                      const data = personMonthData[person.id]?.[month] ?? {};
                      const val = data[rt.key] ?? 0;
                      if (rt.key === 'night') {
                        const isEd =
                          editingNight?.personId === person.id &&
                          editingNight?.month === month;
                        return (
                          <td key={month} className="cell-value cell-night-editable">
                            {isEd ? (
                              <input
                                type="text"
                                className="night-hours-input"
                                value={nightDraft}
                                onChange={(e) => setNightDraft(e.target.value)}
                                onBlur={() => commitNightEdit(person.id, month)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.currentTarget.blur();
                                  if (e.key === 'Escape') {
                                    setEditingNight(null);
                                    setNightDraft('');
                                  }
                                }}
                                autoFocus
                                aria-label={`Noćni sati ${person.name} ${month}. mjesec`}
                              />
                            ) : (
                              <button
                                type="button"
                                className="night-hours-hit"
                                onClick={() => {
                                  setEditingNight({ personId: person.id, month });
                                  setNightDraft(
                                    val ? String(val).replace('.', ',') : ''
                                  );
                                }}
                              >
                                {formatDecimalHoursSigned(val)}
                              </button>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={month} className="cell-value">
                          {formatDecimalHoursSigned(val)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isNightImportModalOpen}
        onRequestClose={() => setIsNightImportModalOpen(false)}
        contentLabel="Uvoz noćnih sati"
        className="modal mjesecni-night-import-modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <h2>Uvoz noćnih sati (.xlsx/.csv)</h2>
          <button
            type="button"
            onClick={() => setIsNightImportModalOpen(false)}
            className="close-modal-button"
          >
            ×
          </button>
        </div>
        <p className="mjesecni-night-import-hint">
          Odaberi firmu i mjesec za koji uvoziš noćne sate. ID broj u datoteci mora odgovarati Glovo ili Wolt ID-u
          osobe u toj firmi. Godina je ista kao u filtru stranice ({selectedYear}).
        </p>
        <div className="mjesecni-night-import-form">
          <label>
            Firma (obavezno)
            <select
              value={nightImportFirmId}
              onChange={(ev) => setNightImportFirmId(ev.target.value)}
              aria-label="Firma za uvoz noćnih sati"
            >
              <option value="">— Odaberi firmu —</option>
              {firms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Mjesec uvoza
            <select
              value={nightImportMonth}
              onChange={(ev) => setNightImportMonth(parseInt(ev.target.value, 10))}
              aria-label="Mjesec za uvoz noćnih sati"
            >
              {MONTH_NAMES_LONG.map((name, i) => (
                <option key={i + 1} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mjesecni-night-import-actions">
          <button
            type="button"
            className="mjesecni-sati-import-btn"
            disabled={!nightImportFirmId}
            onClick={() => nightImportFileRef.current?.click()}
          >
            <FaFileImport />
            <span>Odaberi .xlsx ili .csv datoteku</span>
          </button>
          <button
            type="button"
            className="mjesecni-night-import-cancel"
            onClick={() => setIsNightImportModalOpen(false)}
          >
            Odustani
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isNightUploadedModalOpen}
        onRequestClose={() => setIsNightUploadedModalOpen(false)}
        contentLabel="Uvezeni mjeseci noćnih sati"
        className="modal uploaded-weeks-modal mjesecni-night-uploaded-modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <h2>Uvezeni mjeseci (noćni sati)</h2>
          <button
            type="button"
            onClick={() => setIsNightUploadedModalOpen(false)}
            className="close-modal-button"
          >
            ×
          </button>
        </div>
        <p className="mjesecni-night-uploaded-range-hint">
          Prikaz za godinu {selectedYear - 1}–{selectedYear + 1} (kao i učitani dani na stranici Sati).
        </p>
        {uploadedNightMonths.length === 0 ? (
          <p className="no-data">Nema zapisanih uvezenih mjeseci.</p>
        ) : (
          <ul className="uploaded-days-list uploaded-night-months-list">
            {[...uploadedNightMonths]
              .sort((a, b) => {
                if (a.year !== b.year) return b.year - a.year;
                if (a.month !== b.month) return b.month - a.month;
                return getFirmName(a.firmId).localeCompare(getFirmName(b.firmId));
              })
              .map((row) => (
                <li key={row.id} className="uploaded-day-item uploaded-night-month-item">
                  <span className="day-info">
                    {getFirmName(row.firmId)} – {MONTH_NAMES_LONG[row.month - 1]} {row.year}
                  </span>
                  <span className="day-filename">
                    {row.filename || '—'} · {formatFirestoreDate(row.importedAt)}
                    {row.peopleUpdated != null ? ` · ${row.peopleUpdated} osoba` : ''}
                  </span>
                  <button
                    type="button"
                    className="delete-week-button"
                    onClick={() => deleteUploadedNightMonth(row)}
                  >
                    Izbriši
                  </button>
                </li>
              ))}
          </ul>
        )}
      </Modal>
    </div>
  );
}

export default MjesecniSati;
